"use client";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { loginReduxState, logoutReduxState } from "@/src/store/auth";
import { setFavoriteList, handleListingFavorited } from "@/src/store/favorite";

/* import { setCookie } from "helpers/cookie"; */
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { getToken } from "helpers/auth";
import { isListingUrlControl } from "helpers/url";
import { clearUserSessionCookies, setUserSessionCookies } from "@/src/helpers/user-session-cookies";
import { deleteCookie } from "@/src/helpers/cookie";
import { GTMPushEvent } from "@/src/helpers/google-tag-manager";
import { gtmLoadInterval } from "helpers/google-tag-manager/gtm-load-interval";

import { useSetEnv } from "@/src/utils/setEnv";

import headerMock, { headerDisabledRoutes } from "mock/header";
import { HASHED_MAIL } from "@/src/utils/constants/cookie-keys";
import { arsaBannerExternalLinks } from "@/constants/arsa-banner";

import IconLogo from "assets/svg/logo.svg";
import IconArrow from "assets/svg/chevron-down.svg";

import HeaderMenu from "components/header-menu";
import HeaderMenuSub from "components/header-menu-sub";
import Button from "components/common/button";
import Loading from "components/common/loading-spinner";

import { headerMembershipButton } from "@/constants/test-selector-ids";

import styles from "./styles.module.css";

const Header = ({ categoryTree }) => {
  useSetEnv();
  const pathname = usePathname();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector((state) => state.auth.isAuth);

  const [isUserMenuOpened, setIsUserMenuOpened] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [userData, setUserData] = useState();
  const [loading, setLoading] = useState(true);

  const isHomePage = pathname === "/";
  const isStickyControl = isListingUrlControl(pathname);

  let stored = null;
  if (typeof window !== "undefined") {
    stored = localStorage.getItem("user-data") || null;
  }

  const isHeaderHidden = headerDisabledRoutes.some((route) => pathname.startsWith(route));
  if (isHeaderHidden) return null;

  const handleTriggerSubMenu = (e, directive) => {
    e.stopPropagation();
    setIsUserMenuOpened(!directive ? !isUserMenuOpened : false);
  };

  const logoutHeader = async () => {
    try {
      Ga4WithEventParameters({
        eventName: "header",
        eventParameters: { label: "click||profil||cikis-yap" },
      });
      // setCookie("isLogin", 0, 9999);
      deleteCookie(HASHED_MAIL);
      await localStorage.removeItem("user-data");
      await localStorage.removeItem("Authorization");
      await localStorage.removeItem("RefreshToken");
      await localStorage.removeItem("related-login-sent");
      setIsAuth(false);
      setUserData();
      setIsUserMenuOpened(false);
      dispatch(logoutReduxState());
      dispatch(setFavoriteList([]));
      dispatch(handleListingFavorited(false));
    } catch (error) {
      console.error("Error accessing local storage on logout.");
    }
  };

  const handleSetGa4Event = () => {
    const conditionalLabelForUserIsAuth = isAuth ? "panele-git" : "ucretsiz-ilan-ver";
    Ga4WithEventParameters({
      eventName: "header",
      eventParameters: { label: `click||${conditionalLabelForUserIsAuth}` },
    });
  };

  const gtmPushRelated = () => {
    const relatedLoginStatus = localStorage.getItem("related-login-sent") === "true";
    if (!relatedLoginStatus) {
      gtmLoadInterval(() => {
        GTMPushEvent({
          event: "related_event",
          event_name: "related_login",
          event_parameters: {
            user_id: JSON.parse(localStorage.getItem("user-data")).id,
          },
        });
        localStorage.setItem("related-login-sent", "true");
      });
    }
  };

  useEffect(() => {
    if (stored !== null) {
      setUserData(JSON.parse(stored));
      setIsAuth(!!stored);
      dispatch(loginReduxState());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setUserData(JSON.parse(localStorage.getItem("user-data")));
    setIsAuth(isAuthenticated);
    if (isAuthenticated) {
      setUserSessionCookies(JSON.parse(localStorage.getItem("user-data")));
    } else {
      clearUserSessionCookies();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuth && pathname !== "/giris" && pathname !== "/giris/") {
      gtmPushRelated();
    }
  }, [isAuth]);

  const headerClasses = classNames({
    [styles.wrapper]: true,
    [styles.wrapperIsSticky]: isStickyControl,
  });

  return (
    <>
      {isHomePage && (
        <a target="_blank" href={arsaBannerExternalLinks.youtubeLink} rel="noreferrer">
          <img src={arsaBannerExternalLinks.imageSource} alt="banner-logo" />
        </a>
      )}
      <header className={headerClasses}>
        <div className={styles.inner}>
          <a href="/" className={styles.logoWrap}>
            <IconLogo />
          </a>
          <HeaderMenu categoryTree={categoryTree} />
        </div>
        {loading ? (
          <div className={styles.loading}>
            <Loading loadingClass={styles.loadingClass} />
          </div>
        ) : (
          <div className={styles.actionWrap}>
            <div className={styles.subscriptionWrap}>
              <a
                role="button"
                onClick={(e) => handleTriggerSubMenu(e)}
                onBlur={(e) => {
                  handleTriggerSubMenu(e, "close");
                }}
                className={classNames({
                  [styles.highlighter]: true,
                  [styles.highlighterForUser]: isAuth,
                })}
                data-test-selector={headerMembershipButton}
              >
                {isAuth ? userData?.fullName : "Üyelik"}
                <span
                  className={classNames({
                    [styles.icon]: true,
                    [styles.iconTransformed]: isUserMenuOpened,
                    [styles.iconDark]: isAuth,
                  })}
                >
                  <IconArrow />
                </span>
              </a>
              <HeaderMenuSub
                isOpen={isUserMenuOpened}
                isUserMenu={!isAuth}
                logout={logoutHeader}
                data={headerMock[isAuth ? "authUser" : "subscription"]}
                isAuth={isAuth}
              />
            </div>
            <div className={styles.buttonWrap}>
              <a
                href={
                  isAuth
                    ? `${window.env.EJ_APPS_PANEL_HOSTNAME}/#/member/authorize?token=${getToken()}`
                    : "/uye-ol/bireysel"
                }
                target={isAuth ? "_blank" : "_self"}
                rel="noreferrer"
              >
                <Button size="M" onClick={handleSetGa4Event}>
                  {isAuth && userData?.accountType === "CORPORATE" ? "Panele Git" : "Ücretsiz İlan Ver"}
                </Button>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

Header.propTypes = {
  categoryTree: PropTypes.array.isRequired,
  isAuth: PropTypes.bool,
};

export default Header;
